import {
  DEFAULT_PAYMENT_TERMS,
  getMissionPaymentTermsBasis,
  getMissionPaymentTermsDays,
  type PaymentTermsBasis,
} from '@/constants/missionPaymentTerms'
import countriesData from '@/data/countries.json'

/** EU member states (ISO 3166-1 alpha-2) — used for foreign freelancer invoice mentions. */
const EU_MEMBER_COUNTRY_CODES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
])

/**
 * CRA invoicing step — explicit VAT regime choice (never pre-filled).
 * Emission parity with nextjs: resolveCraVatEmissionTraits ↔ resolveVatFromInvoicingOptions
 * (__tests__/lib/e-invoicing/craVatSelectionParity.test.ts)
 */
export type CraVatSelectionId =
  | 'standard_20'
  | 'standard_10'
  | 'standard_5_5'
  | 'standard_2_1'
  | 'franchise_293b'
  | 'other_zero_vat'
  | 'foreign_non_taxable'
  | 'exempt_activity'
  | 'custom_rate'

export const CRA_STANDARD_VAT_RATES = [20, 10, 5.5, 2.1] as const

export const CRA_VAT_SELECTION_GROUPS: {
  groupKey: 'standardRates' | 'zeroVat' | 'other'
  optionIds: CraVatSelectionId[]
}[] = [
  {
    groupKey: 'standardRates',
    optionIds: ['standard_20', 'standard_10', 'standard_5_5', 'standard_2_1'],
  },
  {
    groupKey: 'zeroVat',
    optionIds: ['franchise_293b', 'other_zero_vat', 'foreign_non_taxable'],
  },
  { groupKey: 'other', optionIds: ['custom_rate'] },
]

export type CraVatRegime = 'standard' | 'franchise_293b' | 'non_taxable_256'

export interface CraVatSelectionResolved {
  id: CraVatSelectionId
  subjectToVat: boolean
  vatRate: number
  vatRegime: CraVatRegime
  requiresSelfInvoiceUpload: boolean
}

export const CRA_VAT_SELECTION_IDS: CraVatSelectionId[] = [
  'standard_20',
  'standard_10',
  'standard_5_5',
  'standard_2_1',
  'franchise_293b',
  'other_zero_vat',
  'foreign_non_taxable',
  'exempt_activity',
  'custom_rate',
]

/** Zero-VAT UI selections (incl. legacy exempt_activity on older CRAs). */
export const CRA_ZERO_VAT_SELECTION_IDS = [
  'franchise_293b',
  'other_zero_vat',
  'foreign_non_taxable',
  'exempt_activity',
] as const satisfies readonly CraVatSelectionId[]

const LEGACY_VAT_SELECTION_ALIASES: Record<string, CraVatSelectionId> = {
  btp_reverse_charge: 'other_zero_vat',
  exempt_activity: 'other_zero_vat',
}

function normalizeStoredCraVatSelectionId(value: unknown): CraVatSelectionId | null {
  if (typeof value !== 'string') return null
  const aliased = LEGACY_VAT_SELECTION_ALIASES[value]
  if (aliased) return aliased
  return isCraVatSelectionId(value) ? value : null
}

export type CraZeroVatSelectionId = (typeof CRA_ZERO_VAT_SELECTION_IDS)[number]

export function isCraZeroVatSelectionId(
  value: CraVatSelectionId | null | undefined,
): value is CraZeroVatSelectionId {
  return (
    value != null &&
    (CRA_ZERO_VAT_SELECTION_IDS as readonly CraVatSelectionId[]).includes(value)
  )
}

const RESOLVED_BY_ID: Record<CraVatSelectionId, CraVatSelectionResolved> = {
  standard_20: {
    id: 'standard_20',
    subjectToVat: true,
    vatRate: 20,
    vatRegime: 'standard',
    requiresSelfInvoiceUpload: false,
  },
  standard_10: {
    id: 'standard_10',
    subjectToVat: true,
    vatRate: 10,
    vatRegime: 'standard',
    requiresSelfInvoiceUpload: false,
  },
  'standard_5_5': {
    id: 'standard_5_5',
    subjectToVat: true,
    vatRate: 5.5,
    vatRegime: 'standard',
    requiresSelfInvoiceUpload: false,
  },
  'standard_2_1': {
    id: 'standard_2_1',
    subjectToVat: true,
    vatRate: 2.1,
    vatRegime: 'standard',
    requiresSelfInvoiceUpload: false,
  },
  franchise_293b: {
    id: 'franchise_293b',
    subjectToVat: false,
    vatRate: 0,
    vatRegime: 'franchise_293b',
    requiresSelfInvoiceUpload: false,
  },
  other_zero_vat: {
    id: 'other_zero_vat',
    subjectToVat: false,
    vatRate: 0,
    vatRegime: 'non_taxable_256',
    requiresSelfInvoiceUpload: true,
  },
  foreign_non_taxable: {
    id: 'foreign_non_taxable',
    subjectToVat: false,
    vatRate: 0,
    vatRegime: 'non_taxable_256',
    requiresSelfInvoiceUpload: true,
  },
  exempt_activity: {
    id: 'exempt_activity',
    subjectToVat: false,
    vatRate: 0,
    vatRegime: 'non_taxable_256',
    requiresSelfInvoiceUpload: true,
  },
  custom_rate: {
    id: 'custom_rate',
    subjectToVat: true,
    vatRate: 0,
    vatRegime: 'standard',
    requiresSelfInvoiceUpload: false,
  },
}

export function isFranceCraCountry(country: string | null | undefined): boolean {
  if (!country) return false
  const normalized = country.trim().toLowerCase()
  return normalized === 'france' || normalized === 'fr' || normalized === 'fra'
}

export function normalizeCountryToIsoCode(country: string | null | undefined): string | null {
  if (!country) return null
  const trimmed = country.trim()
  if (/^[a-z]{2}$/i.test(trimmed)) return trimmed.toUpperCase()
  const lower = trimmed.toLowerCase()
  const found = countriesData.countries.find(
    (entry) =>
      entry.name_fr.toLowerCase() === lower ||
      entry.name_en.toLowerCase() === lower ||
      entry.code.toLowerCase() === lower,
  )
  return found?.code ?? null
}

/** True when the freelancer is established in an EU member state (incl. France). */
export function isEuMemberCountry(country: string | null | undefined): boolean {
  const code = normalizeCountryToIsoCode(country)
  return code != null && EU_MEMBER_COUNTRY_CODES.has(code)
}

export interface CraVatOptionFilterContext {
  /**
   * Folkyn emits on CRA approval — French 0% needs franchise vs other sub-choice.
   * Legacy PDF / foreign paths: single 0% click (no « Régime applicable » column).
   */
  requiresZeroVatRegimeSubChoice?: boolean
}

/**
 * Zero-VAT options depend on where the freelancer is established and product path:
 * - France + e-invoice team → franchise en base or other 0% (other switches to PDF)
 * - France + legacy PDF → franchise 0% only, one click (mentions on uploaded invoice)
 * - Abroad → foreign_non_taxable (no sub-reason)
 */
export function isCraVatOptionAvailableForFreelancer(
  optionId: CraVatSelectionId,
  freelancerCountry: string | null | undefined,
  context: CraVatOptionFilterContext = {},
): boolean {
  const countryKnown = Boolean(freelancerCountry?.trim())
  const isFrance = countryKnown && isFranceCraCountry(freelancerCountry)
  const requiresZeroVatRegimeSubChoice = context.requiresZeroVatRegimeSubChoice ?? true

  if (optionId === 'foreign_non_taxable') {
    return countryKnown && !isFrance
  }
  if (optionId === 'franchise_293b') {
    return !countryKnown || isFrance
  }
  if (optionId === 'other_zero_vat') {
    return (!countryKnown || isFrance) && requiresZeroVatRegimeSubChoice
  }
  return true
}

export function filterCraVatOptionsForFreelancer(
  optionIds: CraVatSelectionId[],
  freelancerCountry: string | null | undefined,
  context: CraVatOptionFilterContext = {},
): CraVatSelectionId[] {
  return optionIds.filter((id) =>
    isCraVatOptionAvailableForFreelancer(id, freelancerCountry, context),
  )
}

export function resolveCraVatLegalMentionKey(
  selectionId: CraVatSelectionId,
  freelancerCountry?: string | null,
): string {
  if (selectionId === 'foreign_non_taxable') {
    return isEuMemberCountry(freelancerCountry)
      ? 'activityReport.create.invoicing.legalMentions.vat.foreign_non_taxable_eu'
      : 'activityReport.create.invoicing.legalMentions.vat.foreign_non_taxable_non_eu'
  }
  return `activityReport.create.invoicing.legalMentions.vat.${selectionId}`
}

/**
 * Statutory VAT text for the Step 3 « Invoice Mentions » block — Folkyn e-invoice only.
 * PDF paths (foreign, other 0 %, legal exemption) omit it; the freelancer puts mentions on their PDF.
 */
export function craVatSelectionRequiresInvoiceLegalMention(
  selectionId: CraVatSelectionId | null | undefined,
): boolean {
  return selectionId === 'franchise_293b'
}

export function readCraCustomVatRate(
  ownInvoice: Record<string, unknown> | null | undefined,
): number | null {
  const rate = Number(ownInvoice?.vatRate ?? ownInvoice?.vat_rate ?? NaN)
  if (!Number.isFinite(rate) || rate <= 0 || rate > 100) return null
  return rate
}

export function isValidCraCustomVatRate(rate: unknown): rate is number {
  const n = Number(rate)
  return Number.isFinite(n) && n > 0 && n <= 100
}

/** Parse typed custom VAT rate — comma or dot; rejects incomplete values like "8." while typing. */
export function parseCraCustomVatRateInput(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  if (/[.,]$/.test(trimmed)) return null
  const normalized = trimmed.replace(',', '.')
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null
  const n = Number(normalized)
  return isValidCraCustomVatRate(n) ? n : null
}

export function isCraVatSelectionId(value: unknown): value is CraVatSelectionId {
  return typeof value === 'string' && CRA_VAT_SELECTION_IDS.includes(value as CraVatSelectionId)
}

export function resolveCraVatSelection(
  selectionId: CraVatSelectionId | null | undefined,
): CraVatSelectionResolved | null {
  if (!selectionId || !isCraVatSelectionId(selectionId)) return null
  return RESOLVED_BY_ID[selectionId]
}

export function readCraVatSelectionFromOwnInvoice(
  ownInvoice: Record<string, unknown> | null | undefined,
): CraVatSelectionId | null {
  const direct = normalizeStoredCraVatSelectionId(
    ownInvoice?.vatSelection ?? ownInvoice?.vat_selection,
  )
  if (direct) return direct

  const regime = ownInvoice?.vatRegime ?? ownInvoice?.vat_regime
  if (regime === 'franchise_293b') return 'franchise_293b'
  if (regime === 'reverse_charge_btp') return 'other_zero_vat'
  if (regime === 'non_taxable_256' || regime === 'exempt_activity') {
    const directSelection = ownInvoice?.vatSelection ?? ownInvoice?.vat_selection
    if (directSelection === 'foreign_non_taxable') return 'foreign_non_taxable'
    if (directSelection === 'other_zero_vat') return 'other_zero_vat'
    if (directSelection === 'exempt_activity') return 'exempt_activity'
    return 'other_zero_vat'
  }

  const rate = Number(ownInvoice?.vatRate ?? ownInvoice?.vat_rate ?? NaN)
  const subjectToVat = ownInvoice?.subjectToVat ?? ownInvoice?.subject_to_vat
  if (subjectToVat === false && rate === 0) return null

  if (subjectToVat && rate === 20) return 'standard_20'
  if (subjectToVat && rate === 10) return 'standard_10'
  if (subjectToVat && rate === 5.5) return 'standard_5_5'
  if (subjectToVat && rate === 2.1) return 'standard_2_1'

  return null
}

/** Explicit user choice only — never inferred from legacy subject_to_vat / vat_rate fields. */
export function readExplicitCraVatSelectionFromOwnInvoice(
  ownInvoice: Record<string, unknown> | null | undefined,
): CraVatSelectionId | null {
  return normalizeStoredCraVatSelectionId(ownInvoice?.vatSelection ?? ownInvoice?.vat_selection)
}

/**
 * Strip legacy / inferred VAT metadata when the user has not made an explicit
 * vat_selection choice on this CRA. Prevents profile-era subject_to_vat + vat_rate
 * from looking like a pre-filled regime on a new report.
 */
export function sanitizeCraOwnInvoiceVatFields(
  ownInvoice: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const own = ownInvoice && typeof ownInvoice === 'object' ? { ...ownInvoice } : {}
  if (readExplicitCraVatSelectionFromOwnInvoice(own)) {
    return own
  }
  return patchOwnInvoiceClearVatSelection(own)
}

export function isCraVatSelectionComplete(
  ownInvoice: Record<string, unknown> | null | undefined,
): boolean {
  const selection = readExplicitCraVatSelectionFromOwnInvoice(ownInvoice)
  if (!selection) return false
  if (selection === 'custom_rate') {
    return readCraCustomVatRate(ownInvoice) != null
  }
  return true
}

export type CraVatNumberRequirementContext = {
  /**
   * True only when Folkyn emits the e-invoice (Super PDP / `folkyn_emits`).
   * When the freelancer uploads their own PDF (`freelancer_pdf` — foreign, classic team,
   * « Autre 0 % », opt-out), no profile VAT number is required: the invoice they deposit
   * already carries whatever VAT identifiers they need.
   */
  folkynEmits?: boolean
}

/**
 * Standard VAT on a Folkyn-emitted CRA requires a freelancer profile VAT number (Super PDP).
 * Not required when the freelancer deposits their own invoice PDF.
 */
export function craInvoicingRequiresFreelancerVatNumber(
  ownInvoice: Record<string, unknown> | null | undefined,
  context?: CraVatNumberRequirementContext,
): boolean {
  if (context?.folkynEmits === false) return false
  const selectionId = readExplicitCraVatSelectionFromOwnInvoice(ownInvoice)
  if (selectionId === 'custom_rate') {
    return readCraCustomVatRate(ownInvoice) != null
  }
  const resolved = resolveCraVatSelection(selectionId)
  if (!resolved) return false
  return resolved.vatRegime === 'standard' && resolved.subjectToVat
}

export function isCraFreelancerVatProfileReadyForInvoicing(
  ownInvoice: Record<string, unknown> | null | undefined,
  freelancerVatNumber: string | null | undefined,
  context?: CraVatNumberRequirementContext,
): boolean {
  if (!craInvoicingRequiresFreelancerVatNumber(ownInvoice, context)) return true
  const normalized = (freelancerVatNumber ?? '').trim().toUpperCase().replace(/\s/g, '')
  return normalized.length > 0
}

export interface CraVatEmissionTraits {
  vatRegime: CraVatRegime
  vatRate: number
  requiresSelfInvoiceUpload: boolean
}

/**
 * Emission traits aligned with nextjs resolveVatFromInvoicingOptions.
 * Parity contract: __tests__/lib/e-invoicing/craVatSelectionParity.test.ts
 */
export function resolveCraVatEmissionTraits(
  ownInvoice: Record<string, unknown> | null | undefined,
): CraVatEmissionTraits {
  const own = ownInvoice ?? {}
  const selection = readExplicitCraVatSelectionFromOwnInvoice(own)
  const vatRateFromOwn = Number(own.vatRate ?? own.vat_rate ?? 0) || 0
  const storedRegime = own.vatRegime ?? own.vat_regime
  const subjectToVat = own.subjectToVat ?? own.subject_to_vat

  if (selection) {
    if (selection === 'custom_rate') {
      const customRate = readCraCustomVatRate(own)
      if (customRate == null || customRate <= 0) {
        return {
          vatRegime: 'non_taxable_256',
          vatRate: 0,
          requiresSelfInvoiceUpload: true,
        }
      }
      return {
        vatRegime: 'standard',
        vatRate: customRate,
        requiresSelfInvoiceUpload: false,
      }
    }
    const resolved = resolveCraVatSelection(selection)
    if (resolved) {
      return {
        vatRegime: resolved.vatRegime,
        vatRate: resolved.vatRate,
        requiresSelfInvoiceUpload: resolved.requiresSelfInvoiceUpload,
      }
    }
  }

  if (storedRegime === 'franchise_293b') {
    return {
      vatRegime: 'franchise_293b',
      vatRate: 0,
      requiresSelfInvoiceUpload: false,
    }
  }

  if (storedRegime === 'non_taxable_256' || storedRegime === 'reverse_charge_btp') {
    return {
      vatRegime: 'non_taxable_256',
      vatRate: 0,
      requiresSelfInvoiceUpload: true,
    }
  }

  if (subjectToVat === true && vatRateFromOwn > 0) {
    return {
      vatRegime: 'standard',
      vatRate: vatRateFromOwn,
      requiresSelfInvoiceUpload: false,
    }
  }

  if (subjectToVat === false || vatRateFromOwn === 0) {
    return {
      vatRegime: 'non_taxable_256',
      vatRate: 0,
      requiresSelfInvoiceUpload: true,
    }
  }

  return {
    vatRegime: 'standard',
    vatRate: vatRateFromOwn,
    requiresSelfInvoiceUpload: false,
  }
}

export function craRequiresSelfInvoiceUpload(
  ownInvoice: Record<string, unknown> | null | undefined,
): boolean {
  return resolveCraVatEmissionTraits(ownInvoice).requiresSelfInvoiceUpload
}

/** Foreign zero-VAT still requires self-invoice upload, but without redundant UI hints. */
export function shouldShowCraSelfInvoiceUploadHint(
  ownInvoice: Record<string, unknown> | null | undefined,
): boolean {
  const selection = readExplicitCraVatSelectionFromOwnInvoice(ownInvoice)
  if (selection === 'foreign_non_taxable') return false
  if (selection === 'other_zero_vat' || selection === 'exempt_activity') return true
  return craRequiresSelfInvoiceUpload(ownInvoice)
}

/** Effective VAT on CRA invoice amounts from the unified regime dropdown. */
export function resolveCraInvoiceEffectiveVat(
  ownInvoice: Record<string, unknown> | null | undefined,
): { subjectToVat: boolean; vatRate: number } {
  const selectionId = readExplicitCraVatSelectionFromOwnInvoice(ownInvoice)
  if (selectionId === 'custom_rate') {
    const customRate = readCraCustomVatRate(ownInvoice) ?? 0
    return { subjectToVat: customRate > 0, vatRate: customRate }
  }
  const resolved = resolveCraVatSelection(selectionId)
  return {
    subjectToVat: resolved?.subjectToVat ?? false,
    vatRate: resolved?.vatRate ?? 0,
  }
}

/** Effective VAT assujetti for expense refacturation (rate > 0 required). */
export function resolveCraFreelancerAssujetti(
  ownInvoice: Record<string, unknown> | null | undefined,
): boolean {
  const selection = readExplicitCraVatSelectionFromOwnInvoice(ownInvoice)
  if (!selection) return false
  if (selection === 'custom_rate') {
    return (readCraCustomVatRate(ownInvoice) ?? 0) > 0
  }
  const resolved = resolveCraVatSelection(selection)
  return Boolean(resolved?.subjectToVat && resolved.vatRate > 0)
}

export function patchOwnInvoiceClearVatSelection(
  ownInvoice: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...ownInvoice,
    vatSelection: null,
    vat_selection: null,
    subjectToVat: false,
    subject_to_vat: false,
    vatRate: 0,
    vat_rate: 0,
    vatRegime: null,
    vat_regime: null,
  }
}

export function patchOwnInvoiceForCraVatSelection(
  ownInvoice: Record<string, unknown>,
  selectionId: CraVatSelectionId,
): Record<string, unknown> {
  const resolved = RESOLVED_BY_ID[selectionId]
  const customRate =
    selectionId === 'custom_rate' ? readCraCustomVatRate(ownInvoice) : null
  const vatRate = selectionId === 'custom_rate' ? (customRate ?? 0) : resolved.vatRate
  return {
    ...ownInvoice,
    vatSelection: selectionId,
    vat_selection: selectionId,
    subjectToVat: selectionId === 'custom_rate' ? vatRate > 0 : resolved.subjectToVat,
    subject_to_vat: selectionId === 'custom_rate' ? vatRate > 0 : resolved.subjectToVat,
    vatRate,
    vat_rate: vatRate,
    vatRegime: resolved.vatRegime,
    vat_regime: resolved.vatRegime,
  }
}

export function patchOwnInvoiceForCraCustomVatRate(
  ownInvoice: Record<string, unknown>,
  vatRate: number,
): Record<string, unknown> {
  return {
    ...ownInvoice,
    vatSelection: 'custom_rate',
    vat_selection: 'custom_rate',
    subjectToVat: true,
    subject_to_vat: true,
    vatRate,
    vat_rate: vatRate,
    vatRegime: 'standard',
    vat_regime: 'standard',
  }
}

type PaymentTermsMissionSource = {
  payment_terms_days?: number | string | null
  paymentTermsDays?: number | string | null
  payment_terms_basis?: string | null
  paymentTermsBasis?: string | null
}

type PaymentTermsCompanySource = {
  default_payment_terms_days?: number | null
  default_payment_terms_basis?: string | null
}

export function resolveCraPaymentTerms(
  mission?: PaymentTermsMissionSource | null,
  companyInfo?: PaymentTermsCompanySource | null,
): { days: number; basis: PaymentTermsBasis } {
  const missionDays = mission?.payment_terms_days ?? mission?.paymentTermsDays
  const days =
    missionDays != null && missionDays !== ''
      ? getMissionPaymentTermsDays(mission)
      : typeof companyInfo?.default_payment_terms_days === 'number'
        ? companyInfo.default_payment_terms_days
        : DEFAULT_PAYMENT_TERMS.days

  const missionBasis = mission?.payment_terms_basis ?? mission?.paymentTermsBasis
  const basis =
    missionBasis === 'end_of_month' || missionBasis === 'invoice_date'
      ? getMissionPaymentTermsBasis(mission)
      : companyInfo?.default_payment_terms_basis === 'end_of_month'
        ? 'end_of_month'
        : DEFAULT_PAYMENT_TERMS.basis

  return { days, basis }
}
